import {
    ReportBuilder,
    ReportBuilderCell,
    ReportBuilderCellDisplay,
    ReportBuilderCellFunctionArgument,
    ReportBuilderCellVariable,
    ReportBuilderParserData,
    ReportBuilderRow,
} from "./s25.document.report.builder.table.component";
import { S25Util } from "../../../util/s25-util";
import { InferSignal } from "../../../pojo/Util";
import { signal } from "@angular/core";
import { VariableWithOptions } from "../../s25-dropdown/single-select/s25.bpe.vars.dropdown.component";
import { BpeVarsConst } from "../../bpe/s25.bpe.const";
import { Doc } from "./s25.document.const";
import { BpeVariableData } from "../../bpe/bpe.vars.component";
import { Item } from "../../../pojo/Item";
import { RoleService } from "../../../services/role.service";
import { CustomAttributeService } from "../../../services/custom.attribute.service";
import { Document, DocumentService } from "../../../services/document.service";
import { DocumentUtil } from "./s25.document.util";
import { StylesUtil } from "../../s25-styles/s25.styles.util";

export namespace ReportBuilderUtil {
    export function getBreakpoints(rows: ReportBuilderRow[]): number[] {
        let breakpoints = rows.flatMap((row) => {
            let sum = 0;
            return row.cells.flatMap((cell) => {
                if (cell.variable().type() === "subreport" && cell.variable().subreport) {
                    // Get breakpoints for rendered subreports
                    const bodyBreakpoints = getBreakpoints(cell.variable().subreport.body);
                    const headerBreakpoints = getBreakpoints(cell.variable().subreport.header);
                    const factor = cell.width() / 100;
                    let breakpoints = bodyBreakpoints.concat(headerBreakpoints).map((bp) => sum + bp * factor);
                    sum += cell.width();
                    return breakpoints;
                }
                sum += cell.width();
                return sum;
            });
        });
        breakpoints = S25Util.array.unique(breakpoints);
        breakpoints = breakpoints.filter((bp) => bp > 0 && bp < 100);
        breakpoints.sort((a, b) => a - b);
        return breakpoints;
    }

    export function newCell(options?: {
        [K in keyof ReportBuilderCell]: InferSignal<ReportBuilderCell[K]>;
    }): ReportBuilderCell {
        const cell: ReportBuilderCell = {
            section: signal(options?.section ?? "header"),
            label: signal(options?.label ?? ""),
            variable: signal(
                options?.variable ?? {
                    type: signal("variable"),
                    state: { options: {} },
                    arguments: signal([]),
                    subreportId: undefined,
                    subreport: undefined,
                },
            ),
            css: signal(options?.css ?? ""),
            width: signal(options?.width ?? 100),
            display: signal(options?.display ?? []),
            valid: signal(options?.valid ?? true),
            errorMessage: signal(options?.errorMessage),
            errors: signal(options?.errors ?? {}),
        };
        cell.display.set(ReportBuilderUtil.getCellDisplayValue(cell));
        return cell;
    }

    export async function parse(tableString: string, type: Doc.ReportBuilderTypeId): Promise<ReportBuilder> {
        if (!tableString) return;

        const [eventRoles, eventAttributes, templateList] = await Promise.all([
            RoleService.getRoles(Item.Ids.Event),
            CustomAttributeService.getCustomAttributes(Item.Ids.Event, true),
            DocumentService.getDocumentTemplateList(),
        ]);

        const parserData: ReportBuilderParserData = {
            eventRole: new Map(),
            eventAttribute: new Map(),
            templates: new Map(),
        };
        for (const role of eventRoles) parserData.eventRole.set(role.role_id, role);
        for (const attribute of eventAttributes?.custom_attributes?.attribute ?? []) {
            parserData.eventAttribute.set(attribute.cust_atrb_id, attribute);
        }
        for (const template of templateList) parserData.templates.set(template.itemId, template);

        return parseWithData(tableString, type, parserData);
    }

    export function parseWithData(
        tableString: string,
        type: Doc.ReportBuilderTypeId,
        parserData: ReportBuilderParserData,
    ): ReportBuilder {
        if (!tableString) return;

        // Unescape ampersands
        tableString = tableString.replace(/&amp;/g, "&");

        const reportBuilder: ReportBuilder = { variable: "", header: [], body: [] };
        for (const { groups } of Array.from(tableString.matchAll(Doc.tableBuilderRegex))) {
            reportBuilder.variable = groups.variable;
            reportBuilder.header = [];
            reportBuilder.body = [];
            const headerRows: Map<number, ReportBuilderRow> = new Map();
            const bodyRows: Map<number, ReportBuilderRow> = new Map();
            const cellMatches = Array.from(groups.table.matchAll(Doc.reportBuilderColumnRegex));
            for (const { groups } of cellMatches) {
                const cell: ReportBuilderCell = ReportBuilderUtil.newCell({
                    section: groups.section as InferSignal<ReportBuilderCell["section"]>,
                    variable: ReportBuilderUtil.parseVariable(groups.variable, type, parserData),
                    label: groups.label,
                    css: groups.style,
                    width: Number(groups.width),
                });
                const rows = groups.section === "header" ? headerRows : bodyRows;
                const row = Number(groups.row);
                if (!rows.has(row)) {
                    const newRow: ReportBuilderRow = { cells: [] };
                    rows.set(row, newRow);
                    (groups.section === "header" ? reportBuilder.header : reportBuilder.body).push(newRow);
                }
                rows.get(row).cells.push(cell);
                ReportBuilderUtil.validateCell(cell, { validateCss: true });
            }
        }

        return reportBuilder;
    }

    export function parseVariable(
        variable: string,
        type: Doc.ReportBuilderTypeId,
        parserData: ReportBuilderParserData,
    ): ReportBuilderCellVariable {
        const ret: ReportBuilderCellVariable = {
            type: signal("variable"),
            state: { variable: undefined, options: {}, var: variable },
            arguments: signal([]),
            subreportId: undefined,
            subreport: undefined,
        };

        const match =
            variable?.match(/^(?<variable>[^.\s|]+)(?:\.(?<id>-?\d+))?(?:\s*\|\s*(?<format>\w+))?/)?.groups ?? {};
        const id = Number(match.id);
        if (id) ret.state.options.id = { val: id };
        if (match.format) {
            ret.state.format = BpeVarsConst.Items.dateFormats.find((format) => format.val === match.format);
        }

        const variableName = match.variable?.match(/^\s*([^.\s|(]+)/)?.[1];
        const variables: VariableWithOptions[] = BpeVarsConst.Items[type].concat(
            BpeVarsConst.Items.reportBuilderFunctions,
        ) as VariableWithOptions[];
        ret.state.variable = variables.find((v) => v.val?.startsWith(variableName));

        const isFunction = /^\s*\w+\(.*\)\s*$/.test(variable);
        if (isFunction) {
            ret.type.set("function");
            const func = variable.split("(")[0];
            const spec = Doc.reportBuilderFunctions[func];
            ret.arguments.set(
                variable
                    .match(/[^(]+\((.*)\)\s*$/)[1]
                    .split(",")
                    .map((variable, i) => {
                        const parsed = parseVariable(variable, type, parserData);

                        let { arg } = ReportBuilderUtil.getArgumentDefinition(spec, parsed.state, i);

                        if (!parsed.state.variable) return { ...arg, text: variable, error: signal(undefined) };
                        return { ...arg, state: parsed.state, error: signal(undefined) };
                    }),
            );
        }

        const isSubreport = /^\s*subreport\.-?\d+/.test(variable);
        if (isSubreport) {
            ret.type.set("subreport");
            ret.state.variable = { val: variable };
            ret.subreportId = Number(variable.split(".")[1]);
            if (ret.subreportId < 0) {
                ret.state.variable.txt = `Template - ${parserData.templates.get(ret.subreportId).itemName}`;
            }
        }

        const options = ret.state.variable?.options;
        if (!!options?.length) {
            if (options.some(DocumentUtil.isEventRole)) {
                ret.state.options.id.txt = parserData?.eventRole?.get(id)?.role_name;
            } else if (options.some(DocumentUtil.isEventAttribute)) {
                ret.state.options.id.txt = parserData?.eventAttribute?.get(id)?.cust_atrb_name;
            }
        }

        return ret;
    }

    export function getArgumentDefinition(
        func: Doc.ReportBuilderFunctionDefinition,
        state: BpeVariableData,
        index: number,
        returnMulti?: boolean,
    ): { arg: Doc.ReportBuilderFunctionArgument; type: "fixed" | "variable" } {
        // Find fixed argument definition
        if (func.fixedArguments?.[index]) {
            const arg = func.fixedArguments[index];

            if (arg.type === "multi") {
                if (returnMulti) return { arg, type: "fixed" };
                return { arg: ReportBuilderUtil.getArgumentDefinitionFromMulti(arg, state), type: "fixed" };
            }
            return { arg, type: "fixed" };
        }
        // Find variable argument definition
        for (const arg of func.variableArguments ?? []) {
            if (arg.type === "multi") {
                const def = ReportBuilderUtil.getArgumentDefinitionFromMulti(arg, state);
                if (def) {
                    if (returnMulti) return { arg, type: "variable" };
                    return { arg: def, type: "variable" };
                }
            }

            if (!state?.variable && arg.type === "text") return { arg, type: "variable" };
            if (
                state.variable &&
                arg.type === "variable" &&
                (!arg.variableTypes || arg.variableTypes.includes(state.variable.type))
            ) {
                return { arg, type: "variable" };
            }
        }
    }

    export function getArgumentDefinitionFromMulti(arg: Doc.ReportBuilderFunctionArgument, state: BpeVariableData) {
        if (!state?.variable) {
            return arg.options.find((opt) => opt.type === "text");
        }
        return arg.options.find((opt) =>
            opt.variableTypes ? opt.variableTypes.includes(state.variable.type) : opt.type === "variable",
        );
    }

    export function getCellDisplayValue(cell: ReportBuilderCell): ReportBuilderCellDisplay[] {
        const display: ReportBuilderCellDisplay[] = [];
        if (cell.label()) display.push({ type: "text", value: cell.label() });
        if (cell.variable().type() === "variable") {
            display.push(getCellVariableDisplayValue(cell.variable().state));
        } else if (cell.variable().type() === "subreport") {
            display.push({ type: "subreport", value: null });
        } else if (cell.variable().type() === "function" && !!cell.variable().state.variable) {
            display.push(...getCellFunctionDisplayValue(cell));
        }

        return display;
    }

    export function getCellVariableDisplayValue(
        state: BpeVariableData,
        options?: DisplayOptions,
    ): ReportBuilderCellDisplay {
        const { hideFormat } = options ?? {};
        if (!state?.variable) return { type: "text", value: "" };

        let value = state.variable.txt ?? state.variable.val;
        if (state.variable.hasFormat && !hideFormat) {
            value += ` (${state.format?.txt || "no format"})`;
        }
        if (state.variable.options?.length) {
            value += ` (${state.options.id?.txt ?? "None"})`;
        }
        return { type: "variable", value: `{{${value}}}` };
    }

    export function getCellFunctionDisplayValue(cell: ReportBuilderCell): ReportBuilderCellDisplay[] {
        const func = cell.variable().state.variable.val;
        const args = cell.variable().arguments();
        switch (func) {
            case "concat": {
                return args.map((arg) => {
                    if (arg.type === "text") return { type: "text", value: arg.text };
                    if (arg.type === "variable") return ReportBuilderUtil.getCellVariableDisplayValue(arg.state);
                    return { type: "text", value: "" };
                });
            }
            case "dateCompare":
            case "dateTimeCompare":
            case "numberCompare":
            case "stringCompare": {
                const [var1, var2, op, ifTrue, ifFalse] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!var1?.state.variable) return display;
                display.push(
                    { type: "functionText", value: "IF " },
                    ReportBuilderUtil.getCellVariableDisplayValue(var1.state, { hideFormat: true }),
                );

                if (!op.text) return display;
                display.push({
                    type: "functionText",
                    value: ` ${Doc.operatorMap[op.text as keyof typeof Doc.operatorMap]} `,
                });

                if (!var2?.state.variable) return display;
                display.push(ReportBuilderUtil.getCellVariableDisplayValue(var2.state, { hideFormat: true }));

                if (ifTrue?.type !== "text" && !ifTrue?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " THEN " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifTrue),
                );

                if (ifFalse?.type !== "text" && !ifFalse?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " ELSE " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifFalse),
                );

                return display;
            }
            case "isNull": {
                const [var1, ifTrue, ifFalse] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!var1?.state.variable) return display;
                display.push(
                    { type: "functionText", value: "IF " },
                    ReportBuilderUtil.getCellVariableDisplayValue(var1.state, { hideFormat: true }),
                    { type: "functionText", value: " IS NULL " },
                );

                if (ifTrue?.type !== "text" && !ifTrue?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " THEN " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifTrue),
                );

                if (ifFalse?.type !== "text" && !ifFalse?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " ELSE " },
                    ReportBuilderUtil.getCellFunctionArgumentDisplayValue(ifFalse),
                );

                return display;
            }
            case "contdCalendarTimes": {
                const [date1, date2] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!date1?.state.variable) return display;
                display.push(ReportBuilderUtil.getCellVariableDisplayValue(date1.state, { hideFormat: true }));

                if (!date2?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " - " },
                    ReportBuilderUtil.getCellVariableDisplayValue(date2.state, { hideFormat: true }),
                );

                return display;
            }
            case "combinePeriodTimes": {
                const [text1, date1, date2, date3] = cell.variable().arguments();
                const display: ReportBuilderCellDisplay[] = [];

                if (!date1?.state.variable) return display;
                display.push(ReportBuilderUtil.getCellVariableDisplayValue(date1.state));

                if (!date2?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " - " },
                    ReportBuilderUtil.getCellVariableDisplayValue(date2.state),
                );

                if (!date3?.state.variable) return display;
                display.push(
                    { type: "functionText", value: " COMPARED WITH " },
                    ReportBuilderUtil.getCellVariableDisplayValue(date2.state, { hideFormat: true }),
                );

                if (!!text1?.text) {
                    display.push({ type: "functionText", value: " ELSE " }, { type: "text", value: text1.text });
                }

                return display;
            }
        }
    }

    export function getCellFunctionArgumentDisplayValue(
        arg: ReportBuilderCellFunctionArgument,
        options?: DisplayOptions,
    ): ReportBuilderCellDisplay {
        if (arg.type === "text") return { type: "text", value: arg.text };
        if (arg.type === "variable") return ReportBuilderUtil.getCellVariableDisplayValue(arg.state, options);
    }

    export function clearVariable(cell: ReportBuilderCell) {
        const newVariable: ReportBuilderCellVariable = {
            type: signal("variable"),
            state: { options: {} },
            arguments: signal([]),
            subreportId: undefined,
            subreport: undefined,
        };
        cell.variable.set(newVariable);
        cell.display.set(ReportBuilderUtil.getCellDisplayValue(cell));
    }

    export function resizeCells(
        widths: number[],
        index: number,
        change: number,
        options?: { minCellWidth?: number; breakpoints?: number[]; snapDistance?: number },
    ): number[] {
        const growingCellIndex = change < 0 ? index + 1 : index;
        let toAdd = Math.abs(change);

        // Snap to breakpoints
        if (options?.breakpoints?.length) {
            const position = S25Util.array.count(widths.slice(0, index + 1), (w) => w) + change;
            const breakpoint = options.breakpoints.reduce((closest, bp) =>
                Math.abs(position - closest) > Math.abs(position - bp) ? bp : closest,
            );
            if (Math.abs(position - breakpoint) <= (options.snapDistance ?? 5))
                toAdd += Math.sign(change) * (breakpoint - position);
        }

        // Resize cells
        let remaining = toAdd;
        const newWidths = [...widths];
        let i = growingCellIndex + Math.sign(change);
        while (i >= 0 && i < widths.length && remaining > 0) {
            newWidths[i] = Math.max(options?.minCellWidth ?? 1, widths[i] - remaining);
            remaining -= widths[i] - newWidths[i];
            i += Math.sign(change);
        }

        newWidths[growingCellIndex] = widths[growingCellIndex] + (toAdd - remaining);

        return newWidths;
    }

    export function stringify(reportBuilder: ReportBuilder, type: Doc.ReportBuilderTypeId) {
        const headerCells = reportBuilder.header.flatMap(stringifyRow).join(" || ");
        const bodyCells = reportBuilder.body
            .flatMap((row, r) => stringifyRow(row, reportBuilder.header.length + r))
            .join(" || ");

        const str = `{{for: ws.flexTable.${type}: ${headerCells ? headerCells + " || " : ""}${bodyCells} :end-for}}`;

        // Escape ampersands
        return str.replace(/&/g, "&amp;");
    }

    export function stringifyRow(row: ReportBuilderRow, rowIndex: number) {
        return row.cells.map((cell) => stringifyCell(cell, rowIndex)).join(" || ");
    }

    export function stringifyCell(cell: ReportBuilderCell, row: number) {
        return `${cell.section()} ^ ${row + 1} ^ ${cell.label() ?? ""} ^ ${stringifyVariable(cell.variable()) ?? ""} ^ ${cell.css() ?? ""} ^ ${cell.width().toFixed(1)}`;
    }

    export function stringifyVariable(variable: ReportBuilderCellVariable) {
        if (variable.type() === "variable") return variable.state.var;
        if (variable.type() === "subreport") return variable.state.var;
        if (variable.type() === "function" && !!variable.state.variable) {
            const args = variable.arguments().map((arg) => {
                switch (arg.type) {
                    case "text":
                    case "comparisonOperator":
                        return arg.text;
                    case "variable":
                        return arg.state.var;
                }
                return "";
            });
            return `${variable.state.variable.val}(${args.join(",")})`;
        }
        return "";
    }

    export function getSubreportIds(tableStr: string, type: Doc.ReportBuilderTypeId): number[] {
        const builder = parseWithData(tableStr, type, null);
        const rows = builder.header.concat(builder.body);
        const subreports = rows
            .flatMap((row) => row.cells)
            .flatMap((cell) => cell.variable().subreportId)
            .filter((id) => id);
        return S25Util.array.unique(subreports);
    }

    export function mapSubreports(document: Document, map: Record<number, number>) {
        if (!DocumentUtil.canReferenceDocuments(document)) return;

        const builder = parseWithData(document.body_text, document.document_type as Doc.ReportBuilderTypeId, null);

        const rows = builder.header.concat(builder.body);
        for (const row of rows) {
            for (const cell of row.cells) {
                if (cell.variable().type() !== "subreport") continue;
                cell.variable().state.var = `subreport.${map[cell.variable().subreportId]}`;
            }
        }

        document.body_text = ReportBuilderUtil.stringify(builder, document.document_type as Doc.ReportBuilderTypeId);
    }

    export function isFunctionArgumentEmpty(arg: ReportBuilderCellFunctionArgument): boolean {
        switch (arg.type) {
            case "text":
            case "comparisonOperator":
                return arg.text === "";
            case "variable":
                return !arg.state.variable;
        }
        return true;
    }

    export function validateCell(cell: ReportBuilderCell, options?: { validateCss?: boolean }): void {
        cell.valid.set(true);
        cell.errorMessage.set(undefined);
        const errors: CellErrors = {};

        if (/[\^]/.test(cell.label())) {
            errors.label = "Label cannot contain ^";
            cell.errorMessage.set("Invalid label");
            cell.valid.set(false);
        }

        if (cell.variable().type() === "function" && !!cell.variable().state.variable) {
            const args = cell.variable().arguments();
            const func = Doc.reportBuilderFunctions[cell.variable().state.variable.val];
            for (const arg of args) {
                arg.error.set(undefined);
                if (arg.type === "text" && /[\^,]/.test(arg.text)) {
                    arg.error.set("Function text cannot contain: ,^");
                }
                if (arg.error()) {
                    cell.errorMessage.set("Invalid function argument");
                    cell.valid.set(false);
                }
            }
            if (func.fixedArguments) {
                // Check that all fixed arguments are filled out
                for (let i = 0; i < func.fixedArguments.length; i++) {
                    const arg = args[i];
                    const spec = func.fixedArguments[i];
                    if (arg && !spec.optional && ReportBuilderUtil.isFunctionArgumentEmpty(arg)) {
                        arg.error.set(`Please select a value`);
                        cell.errorMessage.set("Missing function argument");
                        cell.valid.set(false);
                    }
                }
            }
        }

        if (cell.variable().type() === "subreport" && cell.variable().subreportId < 0) {
            errors.variable = "Template subreports need to be changed before saving";
            cell.errorMessage.set("Invalid subreport");
            cell.valid.set(false);
        }

        if (options?.validateCss) ReportBuilderUtil.validateCss(cell);

        cell.errors.set(errors);
    }

    export function validateCss(cell: ReportBuilderCell) {
        const rule = StylesUtil.parseCssRule(cell.css());
        const cssValid = !StylesUtil.validateCssRule(rule, ReportBuilderUtil.validateCssProperty).error;
        if (!cssValid) {
            cell.errorMessage.set("Invalid styles");
            cell.valid.set(false);
        }
    }

    export function validateCssProperty(property: StylesUtil.CssProperty): string {
        if (!!property.property && /[\^]/.test(property.value)) return "Styles cannot contain ^";
    }

    type DisplayOptions = { hideFormat?: boolean };
    export type CellErrors = { label?: string; variable?: string };
}
